import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CateringCartService } from '../../../../core/services/catering-cart.service';
import { LocaleService } from '../../../../core/services/locale.service';
import { formatCurrencyMinor } from '../../../../core/utils/catering';
import { PaperButtonComponent } from '@ricoh-paper/angular';

/**
 * CartBarComponent — sticky bottom bar showing cart item count and total price.
 *
 * Slides up when items are added to the cart and slides back down when the
 * cart is empty. Emits a viewCart event when the user clicks the action button.
 */
@Component({
  selector: 'ct-cart-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperButtonComponent, TranslocoPipe],
  template: `
    @if (cartService.itemCount() > 0) {
      <div class="cb" data-testid="cart-bar">
        <span class="cb__count" data-testid="cart-bar-count">{{ itemsLabel() }}</span>
        <span class="cb__dot">&middot;</span>
        <span class="cb__total" data-testid="cart-bar-total">{{ formattedTotal() }}</span>
        <paper-button intent="primary" size="sm" data-testid="view-cart-btn" (click)="viewCart.emit()">{{ 'catering.customer.cart_bar.view_cart' | transloco }} &rarr;</paper-button>
      </div>
    }
  `,
  styles: [`
    :host {
      display: block;
      position: sticky;
      bottom: 0;
      z-index: 20;
      margin: 0 -20px -20px; /* bleed to panel edges and bottom */
    }

    .cb {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      background: var(--card-bg-solid);
      border-top: 1px solid var(--border-low);
      animation: cb-slide-up 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    @keyframes cb-slide-up {
      from {
        opacity: 0;
        transform: translateY(100%);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .cb {
        animation: none;
      }
    }

    .cb__count {
      font-size: 13px;
      font-weight: 400;
      color: var(--text);
    }

    .cb__dot {
      color: var(--text-tertiary);
    }

    .cb__total {
      font-size: 13px;
      font-weight: 600;
      color: var(--text);
      font-variant-numeric: tabular-nums;
    }

    .cb paper-button {
      margin-left: auto;
    }
  `],
})
export class CartBarComponent {
  protected readonly cartService = inject(CateringCartService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  readonly viewCart = output<void>();

  protected readonly formattedTotal = computed(() =>
    formatCurrencyMinor(this.cartService.grossPence(), this.cartService.currency()),
  );

  protected readonly itemsLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('catering.customer.cart_bar.items_count', { count: this.cartService.itemCount() });
  });
}
